import React, {useEffect, useRef, useState} from "react";
import arrowDown from "../../assets/images/icons/arrow_circle_bottom.svg";

/*
 * Mapowanie polskich nazw kolorów → hex
 * (Źródło: Figma node 2013:7055)
 */
export const COLOR_HEX_MAP = {
    "biały":        {hex: "#ffffff", border: "#dfdfdf", dark: false},
    "biala":        {hex: "#ffffff", border: "#dfdfdf", dark: false},
    "srebrny":      {hex: "#cecece", dark: false},
    "srebrna":      {hex: "#cecece", dark: false},
    "szary":        {hex: "#6d6d6d", dark: true},
    "szara":        {hex: "#6d6d6d", dark: true},
    "czarny":       {hex: "#000000", dark: true},
    "czarna":       {hex: "#000000", dark: true},
    "czerwony":     {hex: "#ff3636", dark: false},
    "czerwona":     {hex: "#ff3636", dark: false},
    "pomarańczowy": {hex: "#fd9055", dark: false},
    "pomarańczowa": {hex: "#fd9055", dark: false},
    "żółty":        {hex: "#eedc1e", dark: false},
    "żółta":        {hex: "#eedc1e", dark: false},
    "zielony":      {hex: "#73bf63", dark: false},
    "zielona":      {hex: "#73bf63", dark: false},
    "turkusowy":    {hex: "#80dada", dark: false},
    "turkusowa":    {hex: "#80dada", dark: false},
    "niebieski":    {hex: "#6aacdb", dark: false},
    "niebieska":    {hex: "#6aacdb", dark: false},
    "granatowy":    {hex: "#1a3a6b", dark: true},
    "granatowa":    {hex: "#1a3a6b", dark: true},
    "fioletowy":    {hex: "#a959a4", dark: true},
    "fioletowa":    {hex: "#a959a4", dark: true},
    "brązowy":      {hex: "#816453", dark: true},
    "brązowa":      {hex: "#816453", dark: true},
    "beżowy":       {hex: "#f5e6c8", dark: false},
    "beżowa":       {hex: "#f5e6c8", dark: false},
    "złoty":        {hex: "#d4a017", dark: false},
    "złota":        {hex: "#d4a017", dark: false},
    "bordowy":      {hex: "#8b0000", dark: true},
    "bordowa":      {hex: "#8b0000", dark: true},
};

// Normalizuje item — może być stringiem lub obiektem {name: "..."}
function normalizeName(item) {
    if (!item) return "";
    if (typeof item === "object") return item.name || "";
    return String(item);
}

export function getColorData(name) {
    const str = normalizeName(name);
    if (!str) return {hex: "#e0e0e0", dark: false};
    const lower = str.toLowerCase().trim();
    if (COLOR_HEX_MAP[lower]) return COLOR_HEX_MAP[lower];
    for (const key of Object.keys(COLOR_HEX_MAP)) {
        if (lower.includes(key) || key.includes(lower)) return COLOR_HEX_MAP[key];
    }
    return {hex: "#e0e0e0", dark: false};
}

/*
 * ColorPicker — select z kolorowymi kwadratami
 * Wygląda jak dropdown: kliknięcie otwiera siatkę kolorów.
 */
function ColorPicker({items, selectedItems, onChange, placeholder = "Kolor nadwozia"}) {
    const [open, setOpen] = useState(false);
    const [tooltip, setTooltip] = useState(null);
    const containerRef = useRef(null);

    /* zamknij po kliknięciu poza komponentem */
    useEffect(() => {
        const handleOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleOutside);
        return () => document.removeEventListener("mousedown", handleOutside);
    }, []);

    if (!items || items.length === 0) return null;

    // Zawsze pracuj na stringach — selectedItems mogą przyjść jako {name:"..."} z MultipleItemsSearchSelect
    const normalizedSelected = selectedItems.map(normalizeName).filter(Boolean);

    const toggleColor = (colorName) => {
        const name = normalizeName(colorName);
        if (normalizedSelected.includes(name)) {
            onChange(normalizedSelected.filter(c => c !== name));
        } else {
            onChange([...normalizedSelected, name]);
        }
    };

    return (
        <div className="color-select" ref={containerRef}>

            {/* Trigger — wygląd jak select */}
            <div
                className={"color-select__trigger" + (open ? " color-select__trigger--open" : "")}
                onClick={() => setOpen(!open)}
            >
                {normalizedSelected.length === 0 ? (
                    <span className="color-select__placeholder">{placeholder}</span>
                ) : (
                    <div className="color-select__preview">
                        {normalizedSelected.slice(0, 6).map(name => {
                            const cd = getColorData(name);
                            return (
                                <span
                                    key={name}
                                    className="color-select__mini-swatch"
                                    style={{
                                        background: cd.hex,
                                        border: cd.border ? `1px solid ${cd.border}` : "1px solid transparent",
                                    }}
                                />
                            );
                        })}
                        {normalizedSelected.length > 6 && (
                            <span className="color-select__count">+{normalizedSelected.length - 6}</span>
                        )}
                    </div>
                )}
                <img
                    src={arrowDown}
                    className={"color-select__arrow" + (open ? " color-select__arrow--open" : "")}
                    alt="open/close"
                />
            </div>

            {/* Dropdown z siatką kolorów */}
            {open && (
                <div className="color-select__dropdown">
                    <div className="color-select__grid">
                        {items.map(colorName => {
                            const cd = getColorData(colorName);
                            const isSelected = normalizedSelected.includes(normalizeName(colorName));
                            return (
                                <div
                                    key={colorName}
                                    className={"color-select__swatch" + (isSelected ? " color-select__swatch--selected" : "")}
                                    style={{
                                        background: cd.hex,
                                        border: cd.border ? `1px solid ${cd.border}` : "1px solid transparent",
                                    }}
                                    onClick={() => toggleColor(colorName)}
                                    onMouseEnter={() => setTooltip(colorName)}
                                    onMouseLeave={() => setTooltip(null)}
                                >
                                    {isSelected && (
                                        <i
                                            className="fa fa-check"
                                            style={{color: cd.dark ? "#ffffff" : "#333333"}}
                                        ></i>
                                    )}
                                    {tooltip === colorName && (
                                        <div className="color-select__tooltip">
                                            {colorName}
                                            <div className="color-select__tooltip-caret"></div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    {normalizedSelected.length > 0 && (
                        <button type="button" className="color-select__clear" onClick={() => onChange([])}>
                            <i className="fa fa-times"></i> Wyczyść wybór
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

export default ColorPicker;
